import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Save, ArrowLeft, Plus, Trash2 } from "lucide-react";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContractForm({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Propietario
    propietario_nombre: "",
    propietario_rfc: "",
    propietario_telefono: "",
    propietario_email: "",
    propietario_relacion: "",
    
    // Inquilino
    inquilino_nombre: "",
    inquilino_rfc: "",
    inquilino_telefono: "",
    inquilino_email: "",
    inquilino_tipo: "Persona Física",
    
    // Inmueble
    inmueble_nombre: "",
    inmueble_direccion: "",
    inmueble_tipo: "Departamento",
    inmueble_superficie: "",
    
    // Condiciones económicas
    tipo_contrato: "Simple",
    renta_mensual: "",
    moneda: "MXN",
    fecha_inicio: "",
    fecha_termino: "",
    deposito_garantia: "",
    incremento_anual: false,
    incremento_porcentaje: "",
    dia_pago: "1",
    comision_porcentaje: "",
    cuota_condominal: "",
    cuota_condominal_paga: "inquilino",
    
    // Metadata
    fecha_elaboracion: "",
    observaciones: "",
    responsable_interno: "",
    etiquetas: []
  });

  const [fiadores, setFiadores] = useState([]);
  const [archivosParaSubir, setArchivosParaSubir] = useState([]);
  const [actualizarCobranza, setActualizarCobranza] = useState(false);
  const [rentaOriginal, setRentaOriginal] = useState(null);

  useEffect(() => {
    if (isEdit) {
      fetchContract();
    }
  }, [id]);

  const fetchContract = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/contracts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const contract = response.data;
      
      // Guardar renta original para comparar
      setRentaOriginal(contract.renta_mensual);
      
      setFormData({
        propietario_nombre: contract.propietario_nombre,
        propietario_rfc: contract.propietario_rfc || "",
        propietario_telefono: contract.propietario_telefono,
        propietario_email: contract.propietario_email,
        propietario_relacion: contract.propietario_relacion || "",
        
        inquilino_nombre: contract.inquilino_nombre,
        inquilino_rfc: contract.inquilino_rfc || "",
        inquilino_telefono: contract.inquilino_telefono,
        inquilino_email: contract.inquilino_email,
        inquilino_tipo: contract.inquilino_tipo || "Persona Física",
        
        inmueble_nombre: contract.inmueble_nombre,
        inmueble_direccion: contract.inmueble_direccion,
        inmueble_tipo: contract.inmueble_tipo,
        inmueble_superficie: contract.inmueble_superficie || "",
        
        tipo_contrato: contract.tipo_contrato || "Simple",
        renta_mensual: contract.renta_mensual,
        moneda: contract.moneda,
        fecha_inicio: contract.fecha_inicio.split('T')[0],
        fecha_termino: contract.fecha_termino.split('T')[0],
        deposito_garantia: contract.deposito_garantia,
        incremento_anual: contract.incremento_anual,
        incremento_porcentaje: contract.incremento_porcentaje || "",
        dia_pago: contract.dia_pago,
        comision_porcentaje: contract.comision_porcentaje ?? "",
        cuota_condominal: contract.cuota_condominal ?? "",
        cuota_condominal_paga: contract.cuota_condominal_paga || "inquilino",
        
        fecha_elaboracion: contract.fecha_elaboracion ? contract.fecha_elaboracion.split('T')[0] : "",
        observaciones: contract.observaciones || "",
        responsable_interno: contract.responsable_interno || "",
        etiquetas: contract.etiquetas || []
      });
      
      setFiadores(contract.fiadores || []);
    } catch (error) {
      toast.error("Error al cargar el contrato");
      navigate("/contratos");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica del lado del cliente
    const requiredFields = [
      { field: 'propietario_nombre', label: 'Nombre del propietario' },
      { field: 'propietario_telefono', label: 'Teléfono del propietario' },
      { field: 'propietario_email', label: 'Email del propietario' },
      { field: 'inquilino_nombre', label: 'Nombre del inquilino' },
      { field: 'inquilino_telefono', label: 'Teléfono del inquilino' },
      { field: 'inquilino_email', label: 'Email del inquilino' },
      { field: 'inmueble_nombre', label: 'Nombre del inmueble' },
      { field: 'inmueble_direccion', label: 'Dirección del inmueble' },
      { field: 'renta_mensual', label: 'Renta mensual' },
      { field: 'fecha_inicio', label: 'Fecha de inicio' },
      { field: 'fecha_termino', label: 'Fecha de término' },
      { field: 'deposito_garantia', label: 'Depósito de garantía' },
    ];
    
    for (const { field, label } of requiredFields) {
      if (!formData[field] || formData[field].toString().trim() === '') {
        toast.error(`El campo "${label}" es requerido`);
        return;
      }
    }
    
    // Validar emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.propietario_email)) {
      toast.error('El email del propietario no es válido');
      return;
    }
    if (!emailRegex.test(formData.inquilino_email)) {
      toast.error('El email del inquilino no es válido');
      return;
    }
    
    // Validar fechas
    if (new Date(formData.fecha_termino) <= new Date(formData.fecha_inicio)) {
      toast.error('La fecha de término debe ser posterior a la fecha de inicio');
      return;
    }
    
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        ...formData,
        renta_mensual: parseFloat(formData.renta_mensual),
        deposito_garantia: parseFloat(formData.deposito_garantia),
        dia_pago: parseInt(formData.dia_pago),
        inmueble_superficie: formData.inmueble_superficie ? parseFloat(formData.inmueble_superficie) : null,
        incremento_porcentaje: formData.incremento_porcentaje ? parseFloat(formData.incremento_porcentaje) : null,
        comision_porcentaje: formData.comision_porcentaje !== "" ? parseFloat(formData.comision_porcentaje) : null,
        cuota_condominal: formData.cuota_condominal !== "" ? parseFloat(formData.cuota_condominal) : null,
        cuota_condominal_paga: formData.cuota_condominal !== "" ? formData.cuota_condominal_paga : null,
        fiadores: fiadores
      };

      let contractId = id;
      
      if (isEdit) {
        const response = await axios.put(
          `${API}/contracts/${contractId}?actualizar_cobranza=${actualizarCobranza}`, 
          payload, 
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        let mensaje = "Contrato actualizado exitosamente";
        if (response.data._cargos_actualizados !== undefined && response.data._cargos_actualizados > 0) {
          mensaje += `\n${response.data._cargos_actualizados} cargo(s) de cobranza actualizados`;
        }
        toast.success(mensaje);
      } else {
        const response = await axios.post(`${API}/contracts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        contractId = response.data.id;
        
        // Upload files if any
        if (archivosParaSubir.length > 0) {
          toast.info(`Subiendo ${archivosParaSubir.length} archivo(s)...`);
          for (const archivo of archivosParaSubir) {
            const formData = new FormData();
            formData.append("file", archivo.file);
            formData.append("titulo", archivo.titulo);
            
            try {
              await axios.post(`${API}/contracts/${contractId}/upload`, formData, {
                headers: { 
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "multipart/form-data"
                }
              });
            } catch (uploadError) {
              console.error("Error uploading file:", uploadError);
              toast.error(`Error al subir ${archivo.titulo}`);
            }
          }
        }
        
        toast.success("Contrato creado exitosamente");
      }
      
      navigate(isEdit ? "/" : `/contratos/${contractId}`);
    } catch (error) {
      console.error("Error al guardar contrato:", error.response?.data);
      const errorDetail = error.response?.data?.detail || "Error al guardar el contrato";
      toast.error(errorDetail);
    } finally {
      setLoading(false);
    }
  };

  const addFiador = () => {
    setFiadores([...fiadores, {
      nombre_completo: "",
      telefono: "",
      email: "",
      tipo_garantia: "Propiedad",
      observaciones: ""
    }]);
  };

  const removeFiador = (index) => {
    setFiadores(fiadores.filter((_, i) => i !== index));
  };

  const updateFiador = (index, field, value) => {
    const updated = [...fiadores];
    updated[index][field] = value;
    setFiadores(updated);
  };

  const addArchivo = () => {
    if (archivosParaSubir.length >= 8) {
      toast.error("Máximo 8 archivos por contrato");
      return;
    }
    setArchivosParaSubir([...archivosParaSubir, {
      id: Date.now(),
      titulo: "",
      file: null
    }]);
  };

  const removeArchivo = (index) => {
    setArchivosParaSubir(archivosParaSubir.filter((_, i) => i !== index));
  };

  const updateArchivo = (index, field, value) => {
    const updated = [...archivosParaSubir];
    updated[index][field] = value;
    setArchivosParaSubir(updated);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isEdit ? "Editar Contrato" : "Nuevo Contrato"}
            </h1>
            <p className="text-gray-500 mt-1">Complete todos los campos requeridos</p>
          </div>
          <Button variant="outline" onClick={() => navigate("/contratos")} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Datos del Propietario */}
          <Card>
            <CardHeader>
              <CardTitle>Datos del Propietario</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="propietario_nombre">Nombre completo *</Label>
                  <Input
                    id="propietario_nombre"
                    data-testid="propietario_nombre"
                    value={formData.propietario_nombre}
                    onChange={(e) => setFormData({ ...formData, propietario_nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="propietario_rfc">RFC</Label>
                  <Input
                    id="propietario_rfc"
                    data-testid="propietario_rfc"
                    value={formData.propietario_rfc}
                    onChange={(e) => setFormData({ ...formData, propietario_rfc: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="propietario_telefono">Teléfono *</Label>
                  <Input
                    id="propietario_telefono"
                    data-testid="propietario_telefono"
                    value={formData.propietario_telefono}
                    onChange={(e) => setFormData({ ...formData, propietario_telefono: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="propietario_email">Email *</Label>
                  <Input
                    id="propietario_email"
                    type="email"
                    data-testid="propietario_email"
                    value={formData.propietario_email}
                    onChange={(e) => setFormData({ ...formData, propietario_email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="propietario_relacion">Relación con la empresa</Label>
                  <Input
                    id="propietario_relacion"
                    data-testid="propietario_relacion"
                    value={formData.propietario_relacion}
                    onChange={(e) => setFormData({ ...formData, propietario_relacion: e.target.value })}
                    placeholder="Cliente, inversionista, etc."
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datos del Inquilino */}
          <Card>
            <CardHeader>
              <CardTitle>Datos del Inquilino</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inquilino_nombre">Nombre completo *</Label>
                  <Input
                    id="inquilino_nombre"
                    data-testid="inquilino_nombre"
                    value={formData.inquilino_nombre}
                    onChange={(e) => setFormData({ ...formData, inquilino_nombre: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquilino_rfc">RFC</Label>
                  <Input
                    id="inquilino_rfc"
                    data-testid="inquilino_rfc"
                    value={formData.inquilino_rfc}
                    onChange={(e) => setFormData({ ...formData, inquilino_rfc: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquilino_telefono">Teléfono *</Label>
                  <Input
                    id="inquilino_telefono"
                    data-testid="inquilino_telefono"
                    value={formData.inquilino_telefono}
                    onChange={(e) => setFormData({ ...formData, inquilino_telefono: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquilino_email">Email *</Label>
                  <Input
                    id="inquilino_email"
                    type="email"
                    data-testid="inquilino_email"
                    value={formData.inquilino_email}
                    onChange={(e) => setFormData({ ...formData, inquilino_email: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inquilino_tipo">Tipo de inquilino</Label>
                  <Select
                    value={formData.inquilino_tipo}
                    onValueChange={(value) => setFormData({ ...formData, inquilino_tipo: value })}
                  >
                    <SelectTrigger id="inquilino_tipo" data-testid="inquilino_tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Persona Física">Persona Física</SelectItem>
                      <SelectItem value="Persona Moral">Persona Moral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Datos del Inmueble */}
          <Card>
            <CardHeader>
              <CardTitle>Datos del Inmueble</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="inmueble_nombre">Nombre del inmueble *</Label>
                  <Input
                    id="inmueble_nombre"
                    data-testid="inmueble_nombre"
                    value={formData.inmueble_nombre}
                    onChange={(e) => setFormData({ ...formData, inmueble_nombre: e.target.value })}
                    placeholder="Ej: Depto 302 - Torre Central Park"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inmueble_tipo">Tipo de inmueble *</Label>
                  <Select
                    value={formData.inmueble_tipo}
                    onValueChange={(value) => setFormData({ ...formData, inmueble_tipo: value })}
                  >
                    <SelectTrigger id="inmueble_tipo" data-testid="inmueble_tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Departamento">Departamento</SelectItem>
                      <SelectItem value="Local">Local</SelectItem>
                      <SelectItem value="Bodega">Bodega</SelectItem>
                      <SelectItem value="Casa">Casa</SelectItem>
                      <SelectItem value="Oficina">Oficina</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="inmueble_direccion">Dirección completa *</Label>
                  <Input
                    id="inmueble_direccion"
                    data-testid="inmueble_direccion"
                    value={formData.inmueble_direccion}
                    onChange={(e) => setFormData({ ...formData, inmueble_direccion: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="inmueble_superficie">Superficie (m²)</Label>
                  <Input
                    id="inmueble_superficie"
                    type="number"
                    step="0.01"
                    data-testid="inmueble_superficie"
                    value={formData.inmueble_superficie}
                    onChange={(e) => setFormData({ ...formData, inmueble_superficie: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Condiciones Económicas */}
          <Card>
            <CardHeader>
              <CardTitle>Condiciones Económicas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipo_contrato">Tipo de contrato *</Label>
                  <Select
                    value={formData.tipo_contrato}
                    onValueChange={(value) => setFormData({ ...formData, tipo_contrato: value })}
                  >
                    <SelectTrigger id="tipo_contrato" data-testid="tipo_contrato">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Simple">Simple</SelectItem>
                      <SelectItem value="De justicia alternativa">De justicia alternativa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moneda">Moneda</Label>
                  <Select
                    value={formData.moneda}
                    onValueChange={(value) => setFormData({ ...formData, moneda: value })}
                  >
                    <SelectTrigger id="moneda" data-testid="moneda">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MXN">MXN</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="renta_mensual">Renta mensual *</Label>
                  <Input
                    id="renta_mensual"
                    type="number"
                    step="0.01"
                    data-testid="renta_mensual"
                    value={formData.renta_mensual}
                    onChange={(e) => setFormData({ ...formData, renta_mensual: e.target.value })}
                    required
                  />
                  {isEdit && rentaOriginal !== null && parseFloat(formData.renta_mensual) !== rentaOriginal && (
                    <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={actualizarCobranza}
                          onChange={(e) => setActualizarCobranza(e.target.checked)}
                          className="w-4 h-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-orange-900">
                          <strong>Actualizar cobranza pendiente</strong>
                          <br />
                          <span className="text-xs text-orange-700">
                            Se actualizarán los cargos de renta mensual que aún no han sido pagados
                          </span>
                        </span>
                      </label>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha_inicio">Fecha de inicio *</Label>
                  <Input
                    id="fecha_inicio"
                    type="date"
                    data-testid="fecha_inicio"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha_termino">Fecha de término *</Label>
                  <Input
                    id="fecha_termino"
                    type="date"
                    data-testid="fecha_termino"
                    value={formData.fecha_termino}
                    onChange={(e) => setFormData({ ...formData, fecha_termino: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="deposito_garantia">Depósito en garantía *</Label>
                  <Input
                    id="deposito_garantia"
                    type="number"
                    step="0.01"
                    data-testid="deposito_garantia"
                    value={formData.deposito_garantia}
                    onChange={(e) => setFormData({ ...formData, deposito_garantia: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dia_pago">Día de pago *</Label>
                  <Input
                    id="dia_pago"
                    type="number"
                    min="1"
                    max="31"
                    data-testid="dia_pago"
                    value={formData.dia_pago}
                    onChange={(e) => setFormData({ ...formData, dia_pago: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="comision_porcentaje">Comisión de administración (%)</Label>
                  <Input
                    id="comision_porcentaje"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="Ej: 8"
                    data-testid="comision_porcentaje"
                    value={formData.comision_porcentaje}
                    onChange={(e) => setFormData({ ...formData, comision_porcentaje: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">Porcentaje que se cobra al propietario sobre la renta</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cuota_condominal">Cuota condominal ($/mes)</Label>
                  <Input
                    id="cuota_condominal"
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="cuota_condominal"
                    value={formData.cuota_condominal}
                    onChange={(e) => setFormData({ ...formData, cuota_condominal: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>¿Quién paga la cuota condominal?</Label>
                  <Select
                    value={formData.cuota_condominal_paga}
                    onValueChange={(v) => setFormData({ ...formData, cuota_condominal_paga: v })}
                  >
                    <SelectTrigger data-testid="cuota_condominal_paga"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inquilino">El inquilino</SelectItem>
                      <SelectItem value="propietario">El propietario directamente</SelectItem>
                      <SelectItem value="administracion">La administración (se descuenta al propietario)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-center">
                  <input
                    id="incremento_anual"
                    type="checkbox"
                    data-testid="incremento_anual"
                    checked={formData.incremento_anual}
                    onChange={(e) => setFormData({ ...formData, incremento_anual: e.target.checked })}
                    className="mr-2 h-4 w-4"
                  />
                  <Label htmlFor="incremento_anual" className="mb-0">Incremento anual</Label>
                </div>
                {formData.incremento_anual && (
                  <div className="space-y-2">
                    <Label htmlFor="incremento_porcentaje">Porcentaje de incremento (%)</Label>
                    <Input
                      id="incremento_porcentaje"
                      type="number"
                      step="0.01"
                      data-testid="incremento_porcentaje"
                      value={formData.incremento_porcentaje}
                      onChange={(e) => setFormData({ ...formData, incremento_porcentaje: e.target.value })}
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Fiadores */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Fiadores</CardTitle>
                <Button type="button" onClick={addFiador} variant="outline" size="sm" data-testid="add-fiador-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Fiador
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {fiadores.length === 0 ? (
                <p className="text-gray-500 text-sm">No hay fiadores agregados</p>
              ) : (
                fiadores.map((fiador, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Fiador {index + 1}</h4>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFiador(index)}
                        data-testid={`remove-fiador-${index}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Nombre completo</Label>
                        <Input
                          value={fiador.nombre_completo}
                          onChange={(e) => updateFiador(index, 'nombre_completo', e.target.value)}
                          data-testid={`fiador-nombre-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Teléfono</Label>
                        <Input
                          value={fiador.telefono}
                          onChange={(e) => updateFiador(index, 'telefono', e.target.value)}
                          data-testid={`fiador-telefono-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={fiador.email}
                          onChange={(e) => updateFiador(index, 'email', e.target.value)}
                          data-testid={`fiador-email-${index}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Tipo de garantía</Label>
                        <Select
                          value={fiador.tipo_garantia}
                          onValueChange={(value) => updateFiador(index, 'tipo_garantia', value)}
                        >
                          <SelectTrigger data-testid={`fiador-tipo-${index}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Propiedad">Propiedad</SelectItem>
                            <SelectItem value="Aval Moral">Aval Moral</SelectItem>
                            <SelectItem value="Póliza">Póliza</SelectItem>
                            <SelectItem value="Obligado Solidario">Obligado Solidario</SelectItem>
                            <SelectItem value="Otro">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Observaciones</Label>
                        <Textarea
                          value={fiador.observaciones}
                          onChange={(e) => updateFiador(index, 'observaciones', e.target.value)}
                          data-testid={`fiador-observaciones-${index}`}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <CardTitle>Información Adicional</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fecha_elaboracion">Fecha de elaboración</Label>
                  <Input
                    id="fecha_elaboracion"
                    type="date"
                    data-testid="fecha_elaboracion"
                    value={formData.fecha_elaboracion}
                    onChange={(e) => setFormData({ ...formData, fecha_elaboracion: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="responsable_interno">Responsable interno</Label>
                  <Input
                    id="responsable_interno"
                    data-testid="responsable_interno"
                    value={formData.responsable_interno}
                    onChange={(e) => setFormData({ ...formData, responsable_interno: e.target.value })}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="observaciones">Observaciones/Notas internas</Label>
                  <Textarea
                    id="observaciones"
                    data-testid="observaciones"
                    value={formData.observaciones}
                    onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                    rows={4}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Archivos para Subir (solo en creación) */}
          {!isEdit && (
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Archivos Adjuntos (Opcional)</CardTitle>
                  <Button 
                    type="button" 
                    onClick={addArchivo} 
                    variant="outline" 
                    size="sm" 
                    disabled={archivosParaSubir.length >= 8}
                    data-testid="add-archivo-button"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Archivo
                  </Button>
                </div>
                {archivosParaSubir.length > 0 && (
                  <p className="text-sm text-gray-500 mt-1">
                    {archivosParaSubir.length} de 8 archivos
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                {archivosParaSubir.length === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No hay archivos para subir. Los archivos se subirán después de crear el contrato.
                  </p>
                ) : (
                  archivosParaSubir.map((archivo, index) => (
                    <div key={archivo.id} className="border rounded-lg p-4 space-y-4">
                      <div className="flex justify-between items-center">
                        <h4 className="font-medium">Archivo {index + 1}</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeArchivo(index)}
                          data-testid={`remove-archivo-${index}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2 md:col-span-2">
                          <Label>Título del archivo *</Label>
                          <Input
                            value={archivo.titulo}
                            onChange={(e) => updateArchivo(index, 'titulo', e.target.value)}
                            placeholder="Ej: Contrato firmado"
                            data-testid={`archivo-titulo-${index}`}
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>Archivo *</Label>
                          <Input
                            type="file"
                            onChange={(e) => updateArchivo(index, 'file', e.target.files[0])}
                            data-testid={`archivo-file-${index}`}
                          />
                          {archivo.file && (
                            <p className="text-sm text-gray-500">
                              {archivo.file.name} ({(archivo.file.size / 1024).toFixed(2)} KB)
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate("/contratos")} data-testid="cancel-button">
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-contract-button">
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Guardando..." : isEdit ? "Actualizar Contrato" : "Crear Contrato"}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
